/**
 * Admin › Inbox: everything a person wrote to us that needs a reply or a
 * decision. Feedback reports (with the reply thread) and account deletion
 * requests. Both panels moved here unchanged from their old index entries.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Message } from "@yagni/shared";
import { Loader2, RefreshCw, Send } from "lucide-react";
import { ChatBubble } from "../../components/InlineChat.js";
import { Button } from "../../ui/button.js";
import { Card } from "../../ui/card.js";
import { Textarea } from "../../ui/textarea.js";
import { FeedbackRow, LoadingSpinner, SubTabs, Table, timeAgo } from "./shared.js";

export type InboxTab = "feedback" | "deletion-requests";

export const INBOX_TABS: Array<{ key: InboxTab; label: string; to: string }> = [
  { key: "feedback", label: "Feedback", to: "/admin/inbox/feedback" },
  { key: "deletion-requests", label: "Deletion requests", to: "/admin/inbox/deletion-requests" },
];

export function InboxPage({ tab, feedbackId }: { tab: InboxTab; feedbackId?: string }) {
  return (
    <div className="space-y-5">
      <SubTabs tabs={INBOX_TABS} active={tab} label="Inbox sections" />
      {tab === "feedback" && <FeedbackPanel initialId={feedbackId} />}
      {tab === "deletion-requests" && <DeletionRequestsPanel />}
    </div>
  );
}

/* ─── Feedback ──────────────────────────────────────────────── */

const FEEDBACK_STATUS_ORDER = ["new", "triaged", "resolved"] as const;
type FeedbackStatus = (typeof FEEDBACK_STATUS_ORDER)[number];

function statusColor(status: FeedbackStatus): string {
  if (status === "new") return "var(--warning)";
  if (status === "triaged") return "var(--info)";
  return "var(--success)";
}

/**
 * Pill-style select for feedback status. Paints the trigger with the same
 * tinted background + colored text used by the inline badges in the list
 * view so the "Status" affordance reads as a live status indicator rather
 * than a plain form field. The filter variant additionally accepts "all"
 * and paints in a neutral tone.
 */
function StatusPillSelect<T extends string>({
  value,
  options,
  onChange,
  disabled,
  color,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
  disabled?: boolean;
  /** Accent color for the pill (background tint + text). */
  color: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      disabled={disabled}
      className="text-[11px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 cursor-pointer appearance-none focus:outline-none"
      style={{
        background: `color-mix(in srgb, ${color} 18%, transparent)`,
        color,
        border: `1px solid color-mix(in srgb, ${color} 35%, transparent)`,
        paddingRight: "1.5rem",
        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(
          color,
        )}' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 0.4rem center",
      }}
    >
      {options.map((opt) => (
        <option
          key={opt.value}
          value={opt.value}
          style={{
            background: "var(--print-bg-solid)",
            color: "var(--text-primary)",
            textTransform: "none",
          }}
        >
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function truncate(text: string | null, max: number): string {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max - 1).trimEnd() + "…";
}

function FeedbackPanel({ initialId }: { initialId?: string }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(initialId ?? null);
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | "all">("all");

  const refresh = useCallback(() => {
    setLoading(true);
    const qs = statusFilter === "all" ? "" : `?status=${statusFilter}`;
    fetch(`/api/admin/feedback${qs}`)
      .then((r) => r.json())
      .then((data: FeedbackRow[]) => {
        setRows(data);
        setSelectedId((prev) => {
          if (prev && data.some((r) => r.id === prev)) return prev;
          return data[0]?.id ?? null;
        });
      })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selected = rows.find((r) => r.id === selectedId) ?? null;

  if (loading && rows.length === 0) return <LoadingSpinner />;

  // Header filter paints in neutral tone when "all" is selected so the
  // unfiltered state doesn't masquerade as a specific status badge.
  const filterColor = statusFilter === "all" ? "var(--text-secondary)" : statusColor(statusFilter);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>
          Status:
        </label>
        <StatusPillSelect<FeedbackStatus | "all">
          value={statusFilter}
          color={filterColor}
          onChange={(next) => setStatusFilter(next)}
          options={[
            { value: "all", label: `All (${rows.length})` },
            { value: "new", label: "New" },
            { value: "triaged", label: "Triaged" },
            { value: "resolved", label: "Resolved" },
          ]}
        />
        <Button
          type="button"
          size="sm"
          onClick={refresh}
          className="ml-auto bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)]/80"
        >
          <RefreshCw size={11} /> Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card variant="muted" padding="lg" className="text-center text-sm text-text-tertiary">
          No feedback reports yet.
        </Card>
      ) : (
        // Fixed height so the inner list + detail panes can each own their
        // own scroll chain (left: rows list, right: conversation history +
        // composer). Every flex-col ancestor in a scroll chain carries
        // min-h-0 so children can shrink below their content size.
        <div className="flex gap-4 rounded-lg border overflow-hidden" style={{ borderColor: "var(--border)", height: "min(70vh, 700px)" }}>
          {/* Left: list */}
          <div
            className="w-80 shrink-0 flex flex-col min-h-0"
            style={{ borderRight: "1px solid var(--print-border)", background: "var(--print-bg)" }}
          >
            <div className="flex-1 overflow-y-auto">
              {rows.map((row) => {
                const isActive = row.id === selectedId;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => {
                      setSelectedId(row.id);
                      navigate(`/admin/inbox/feedback/${encodeURIComponent(row.id)}`, { replace: true });
                    }}
                    className="w-full text-left px-3 py-3 border-b transition-colors"
                    style={{
                      borderColor: "var(--border-subtle)",
                      background: isActive ? "rgba(var(--ink-rgb), 0.05)" : "transparent",
                      cursor: "pointer",
                      border: "none",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                  >
                    <div className="flex items-start gap-2 mb-1">
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-[1px] rounded shrink-0 mt-0.5"
                        style={{
                          background: `color-mix(in srgb, ${statusColor(row.status)} 18%, transparent)`,
                          color: statusColor(row.status),
                        }}
                      >
                        {row.status}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
                          {row.user_display_name ?? row.user_email ?? "Unknown user"}
                        </div>
                        <div className="text-[11px] truncate" style={{ color: "var(--text-tertiary)" }}>
                          {row.workspace_name ?? row.workspace_slug ?? "Unknown workspace"}
                        </div>
                      </div>
                    </div>
                    <div className="text-[12px] leading-snug" style={{ color: "var(--text-secondary)" }}>
                      {truncate(row.first_message, 120) || <em>No message yet</em>}
                    </div>
                    <div className="text-[10px] mt-1" style={{ color: "var(--text-tertiary)" }}>
                      {timeAgo(row.created_at)} · {row.message_count} msg
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: detail */}
          <div className="flex-1 min-w-0 flex flex-col min-h-0" style={{ background: "var(--bg-base)" }}>
            {selected ? (
              <FeedbackDetail
                key={selected.id}
                feedback={selected}
                onMutate={(updated) => {
                  setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
                }}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
                  Select a feedback report to view
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackDetail({ feedback, onMutate }: { feedback: FeedbackRow; onMutate: (updated: FeedbackRow) => void }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [notes, setNotes] = useState(feedback.internal_notes ?? "");
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  useEffect(() => {
    setNotes(feedback.internal_notes ?? "");
    setNotesDirty(false);
  }, [feedback.id, feedback.internal_notes]);

  useEffect(() => {
    let cancelled = false;
    setLoadingMessages(true);
    fetch(`/api/admin/feedback/${feedback.id}/messages`)
      .then((r) => r.json())
      .then((data: Message[]) => {
        if (!cancelled) setMessages(data);
      })
      .finally(() => {
        if (!cancelled) setLoadingMessages(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feedback.id]);

  async function handleStatusChange(next: FeedbackStatus) {
    setStatusSaving(true);
    try {
      const res = await fetch(`/api/admin/feedback/${feedback.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: next }),
      });
      if (res.ok) {
        const updated = (await res.json()) as FeedbackRow;
        onMutate({ ...feedback, ...updated });
      }
    } finally {
      setStatusSaving(false);
    }
  }

  async function handleSaveNotes() {
    setSavingNotes(true);
    try {
      const res = await fetch(`/api/admin/feedback/${feedback.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ internalNotes: notes }),
      });
      if (res.ok) {
        const updated = (await res.json()) as FeedbackRow;
        onMutate({ ...feedback, ...updated });
        setNotesDirty(false);
      }
    } finally {
      setSavingNotes(false);
    }
  }

  async function handleSendReply() {
    const trimmed = replyText.trim();
    if (!trimmed) return;
    setSendingReply(true);
    try {
      const res = await fetch(`/api/admin/feedback/${feedback.id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: trimmed }),
      });
      if (res.ok) {
        const msg = (await res.json()) as Message;
        setMessages((prev) => [...prev, msg]);
        setReplyText("");
        // If the reply bumped the status from new → triaged, reflect it locally.
        if (feedback.status === "new") {
          onMutate({ ...feedback, status: "triaged" });
        }
      }
    } finally {
      setSendingReply(false);
    }
  }

  const debug = feedback.debug_context ?? {};

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Top bar: identity, status, debug */}
      <div className="px-4 py-3 shrink-0" style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold" style={{ color: "var(--text-primary)", fontFamily: "var(--font-display)" }}>
              {feedback.user_display_name ?? feedback.user_email ?? "Unknown user"}
            </div>
            <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {feedback.user_email && <span>{feedback.user_email} · </span>}
              {feedback.workspace_name ?? "—"}
              {feedback.workspace_slug && ` (${feedback.workspace_slug})`} · {timeAgo(feedback.created_at)}
            </div>
          </div>
          <StatusPillSelect<FeedbackStatus>
            value={feedback.status}
            color={statusColor(feedback.status)}
            disabled={statusSaving}
            onChange={(next) => void handleStatusChange(next)}
            options={FEEDBACK_STATUS_ORDER.map((s) => ({ value: s, label: s }))}
          />
        </div>

        {Object.keys(debug).length > 0 && (
          <details className="text-[11px] mt-2" style={{ color: "var(--text-tertiary)" }}>
            <summary className="cursor-pointer select-none">Debug context ({Object.keys(debug).length} fields)</summary>
            <pre
              className="mt-1 p-2 rounded font-mono text-[10px] overflow-x-auto whitespace-pre-wrap"
              style={{ background: "rgba(var(--ink-rgb), 0.04)", color: "var(--text-secondary)" }}
            >
              {JSON.stringify(debug, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* Conversation thread */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
        {loadingMessages ? (
          <LoadingSpinner />
        ) : messages.length === 0 ? (
          <p className="text-sm text-center py-8" style={{ color: "var(--text-tertiary)" }}>
            No messages in this conversation yet.
          </p>
        ) : (
          // Reuse ChatBubble from InlineChat so admin-side rendering stays
          // in lockstep with what the reporting user sees in their panel.
          messages.map((m) => <ChatBubble key={m.id} message={m} agentColor="var(--accent)" />)
        )}
      </div>

      {/* Reply composer */}
      <div className="px-4 py-3 shrink-0" style={{ borderTop: "1px solid var(--print-border)", background: "var(--print-bg)" }}>
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-tertiary)" }}>
          Reply as "Jack from YAGNI"
        </div>
        <div className="flex items-end gap-2 rounded-lg border px-3 py-2" style={{ borderColor: "var(--border)", background: "var(--bg-base)" }}>
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void handleSendReply();
              }
            }}
            placeholder="Reply to the user… (⌘↵ to send)"
            rows={2}
            className="flex-1 resize-none text-[13px] leading-relaxed bg-transparent border-0 max-h-[160px] focus-visible:ring-0"
          />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => void handleSendReply()}
            disabled={!replyText.trim() || sendingReply}
            className={replyText.trim() ? "bg-[var(--warning)] text-white hover:bg-[var(--warning)]/90" : "text-[var(--text-tertiary)]"}
          >
            {sendingReply ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>

        {/* Internal notes */}
        <div className="mt-3">
          <div
            className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center justify-between"
            style={{ color: "var(--text-tertiary)" }}
          >
            <span>Internal notes (not visible to the user)</span>
            {notesDirty && (
              <Button
                type="button"
                size="sm"
                onClick={() => void handleSaveNotes()}
                disabled={savingNotes}
                className="h-auto py-0.5 px-2 text-[10px] bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)]/80"
              >
                {savingNotes ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
          <Textarea
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesDirty(true);
            }}
            placeholder="Triage notes, root cause, next steps…"
            rows={2}
            className="resize-none text-[12px]"
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Deletion requests ─────────────────────────────────────── */

type DeletionStatus = "pending" | "processing" | "completed" | "denied";

interface DeletionRow {
  id: string;
  user_id: string;
  workspace_id: string;
  customer_id: string;
  status: DeletionStatus;
  reason: string | null;
  internal_notes: string | null;
  created_at: string;
  updated_at: string;
  workspace_name: string | null;
  workspace_slug: string | null;
  user_display_name: string | null;
  user_email: string | null;
}

function deletionStatusColor(status: DeletionStatus): string {
  if (status === "pending") return "var(--warning)";
  if (status === "processing") return "var(--info)";
  if (status === "completed") return "var(--success)";
  return "var(--text-secondary)";
}

function DeletionRequestsPanel() {
  const [rows, setRows] = useState<DeletionRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    fetch("/api/admin/deletion-requests")
      .then((r) => r.json())
      .then((data: DeletionRow[]) => setRows(data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function updateStatus(id: string, status: DeletionStatus) {
    try {
      const res = await fetch(`/api/admin/deletion-requests/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        alert(`Failed to mark the request ${status}: ${body.error ?? res.status}`);
        return;
      }
    } catch (err) {
      alert(`Failed to mark the request ${status}: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    refresh();
  }

  if (loading && rows.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center">
        <Button
          type="button"
          size="sm"
          onClick={refresh}
          className="ml-auto bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)]/80"
        >
          <RefreshCw size={11} /> Refresh
        </Button>
      </div>

      {rows.length === 0 ? (
        <Card variant="muted" padding="lg" className="text-center text-sm text-text-tertiary">
          No deletion requests.
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>User</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>Workspace</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>Reason</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>Status</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>Requested</th>
              <th className="text-left py-2 px-3 text-[11px] font-medium" style={{ color: "var(--text-tertiary)" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t" style={{ borderColor: "var(--border)" }}>
                <td className="py-2 px-3">
                  <div className="font-medium">{row.user_display_name ?? "—"}</div>
                  <div className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>{row.user_email}</div>
                </td>
                <td className="py-2 px-3">{row.workspace_name ?? row.workspace_slug ?? "—"}</td>
                <td className="py-2 px-3 max-w-[200px] truncate" title={row.reason ?? undefined}>
                  {row.reason || <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                </td>
                <td className="py-2 px-3">
                  <span
                    className="inline-block px-2 py-0.5 rounded text-[11px] font-medium"
                    style={{
                      color: deletionStatusColor(row.status),
                      background: `color-mix(in srgb, ${deletionStatusColor(row.status)} 12%, transparent)`,
                    }}
                  >
                    {row.status}
                  </span>
                </td>
                <td className="py-2 px-3 text-[12px]" style={{ color: "var(--text-tertiary)" }}>
                  {new Date(row.created_at).toLocaleDateString()}
                </td>
                <td className="py-2 px-3">
                  {row.status === "pending" && (
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        onClick={() => updateStatus(row.id, "processing")}
                        className="h-auto py-0.5 px-2 text-[11px] bg-[var(--info)] hover:bg-[var(--info)]/90"
                      >
                        Process
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => updateStatus(row.id, "denied")}
                        className="h-auto py-0.5 px-2 text-[11px]"
                      >
                        Deny
                      </Button>
                    </div>
                  )}
                  {row.status === "processing" && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(row.id, "completed")}
                      className="h-auto py-0.5 px-2 text-[11px] bg-[var(--success)] hover:bg-[var(--success)]/90"
                    >
                      Mark Complete
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
